import { useCallback, useEffect, useRef } from "react";

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

/**
 * Calls `onIdle` after `timeoutMs` without user activity.
 * Returns `resetTimer` to restart the idle clock (e.g. after "Stay signed in").
 */
export function useIdleTimeout({ timeoutMs, onIdle, enabled = true }) {
  const timeoutRef = useRef(null);
  const onIdleRef = useRef(onIdle);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    clearTimer();
    if (!enabled) {
      return;
    }
    timeoutRef.current = setTimeout(() => {
      onIdleRef.current?.();
    }, timeoutMs);
  }, [clearTimer, enabled, timeoutMs]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return undefined;
    }

    resetTimer();

    const onActivity = () => {
      resetTimer();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
    };
  }, [enabled, resetTimer, clearTimer]);

  return { resetTimer, clearTimer };
}
